import { Button, Col, Layout, Modal, Row, Select, Table, Typography } from 'antd';
import LocaleContext from 'locales';
import _ from 'lodash';
import React, { useContext, useEffect } from 'react';
import { getRequieredProgram } from 'services//websocket/session';
import { getCurrentPackages } from 'services/mgmt';
import { PromiseAllSettled } from 'utils/Polyfills';
import ReqAppConfig from '../config/required_app.config';
import { isWindows, startAgent, vsClagentPortCheck } from '../utils';

/**
 * 서버에 배포된 패키지 목록을 기준으로 다운로드 링크를 추가하여 OS별로 패키지 목록을 리턴한다.
 * ? vsclient는 편집 불가이기 때문에 무조건 있다고 가정한다. 그 외 등록된 패키지는 Windows용으로 가정한다.
 * ? macOS, Linux 클라이언트는 현재 설치 여부를 판별할 수 없기 때문에 무조건 true로 전달한다.
 * @param {array} packages 서버에 설정된 필수 프로그램을 기준으로 설치 유무(state)를 판별한 정보까지 추가한 필수 프로그램 목록
 * @returns {{Windows: [], macOS: [], Linux: []}}
 */
async function packagesByOS(packages, locale) {
  const newPackageData = packages.map((el) => {
    return { ...el.value, os: 'Windows' };
  });

  let currentPackages = {};
  await getCurrentPackages()
    .then((data) => {
      currentPackages = data.versions;
    })
    .catch(() => {
      Modal.warning({
        title: locale.resource['txt.error'],
        content: locale.resource['txt.error-occurred'],
      });
    });

  /**
   * 버전.빌드 넘버를 {version, buildNumber}로 분해
   * @param {string} ver version.buildNumber
   * @returns {version: string, buildNumber: string}
   */
  function divideVersion(pkgStr) {
    const str = currentPackages[pkgStr];
    const lastDotIndex = str.lastIndexOf('.'); // 마지막 .의 위치
    const version = str.substring(0, lastDotIndex);
    const buildNumber = str.substring(lastDotIndex + 1);
    return { version, buildNumber };
  }

  // 배포된 버전에서  macOS, Linux 클라이언트가 배포되었으면 다운로드 데이터 테이블에 추가한다.
  ['vsclient-macos', 'vsclient-linux'].forEach((el) => {
    if (Object.keys(currentPackages).includes(el)) {
      const { version, buildNumber } = divideVersion(el);
      newPackageData.push({
        key: el,
        title: 'txt.client',
        os: el === 'vsclient-macos' ? 'macOS' : 'Linux',
        description: 'txt.install-client',
        downloadPath:
          el === 'vsclient-macos'
            ? `/portal/deploy/vsclient-macos/vsclient-macos-${version}.${buildNumber}.pkg`
            : `/portal/deploy/vsclient-linux/vsclient-linux-${version}.${buildNumber}-amd64.deb`,
        state: null,
      });
    }
  });

  // 배열을 플랫폼(OS)별 데이터로 변환한다.
  const result = newPackageData.reduce((acc, cur) => {
    const { os } = cur;
    acc[os] = [cur];
    return acc;
  }, {});

  return result;
}

function Download() {
  const { locale } = useContext(LocaleContext);
  return (
    <Layout.Content>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Paragraph>
            {locale.resource['txt.install-required-programs']}
          </Typography.Paragraph>
        </Col>
        <Col span={24}>
          <DownloadTable locale={locale} />
        </Col>
      </Row>
    </Layout.Content>
  );
}

export function DownloadTable({ onCheckProgram, ...res }) {
  const { locale } = useContext(LocaleContext);
  const [dataSource, setDataSource] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedOS, setSelectedOS] = React.useState('Windows'); // 현재 선택한 운영체제(OS)

  useEffect(() => {
    if (_.isEmpty(locale.resource)) {
      return;
    }

    setLoading(true);

    // mgmt로 부터 현재 확인해야하는 프로그램 목록을 가져온다.
    const loadingFunc = async () => {
      if (isWindows) {
        const portCheck = await vsClagentPortCheck();
        if (!portCheck) {
          Modal.warning({
            title: locale.resource['task-result.state.check'],
            content: locale.resource['txt.retry-after-vsclagent-check'],
          });
        }
      }
      const programs = await getRequieredProgram();
      return programs;
    };

    loadingFunc().then((programs) => {
      const dataLoading = Object.entries(ReqAppConfig.requiredProgram(programs)).map(
        async ([k, v]) => {
          const state = v.onInstallCheck
            ? (await PromiseAllSettled(await v.onInstallCheck(window)))
                .map((result) => result.value)
                .some(Boolean)
            : true;
          return {
            key: k,
            ...v,
            state,
          };
        },
      );

      PromiseAllSettled(dataLoading).then(async (data) => {
        const result = await packagesByOS(data, locale);
        setDataSource(result);
        setLoading(false);
        onCheckProgram?.(data.map((d) => d.value.state).every(Boolean));
      });
    });
  }, [locale.resource]);

  return (
    <>
      <Select
        style={{ width: 100, marginBottom: 8 }}
        options={Object.keys(dataSource).map((el) => ({ label: el, value: el }))}
        value={selectedOS}
        onChange={(value) => {
          setSelectedOS(value);
        }}
      />
      <Table
        rowKey="key"
        loading={loading}
        pagination={false}
        dataSource={dataSource[selectedOS]}
        columns={[
          {
            title: locale.resource['txt.program-name'],
            dataIndex: 'title',
            width: 160,
            render: (text) => locale.resource[text],
          },
          {
            title: locale.resource['txt.OS'],
            dataIndex: 'os',
            width: 124,
          },
          {
            title: locale.resource['txt.description'],
            dataIndex: 'description',
            render: (text) => locale.resource[text],
          },
          {
            title: locale.resource['txt.status'],
            dataIndex: 'state',
            width: 80,
            render: (text, record) => {
              const agentChk =
                record.key === 'vsclient' ? (
                  <>
                    <Typography.Paragraph>
                      {locale.resource['txt.unknown']}
                      <Typography.Text type="secondary">
                        ({locale.resource['txt.installed-already-run-program']})
                      </Typography.Text>
                    </Typography.Paragraph>
                    {isWindows && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          if (e) {
                            startAgent(2).then(() => {
                              window.location.reload();
                            });
                          }
                        }}
                      >
                        {locale.resource['txt.run-agent']}
                      </Button>
                    )}
                  </>
                ) : (
                  locale.resource['txt.not-installed']
                );
              const stateMap = {
                true: locale.resource['txt.installed'],
                false: agentChk,
                null: '-',
              };
              return stateMap[text];
            },
          },
          {
            title: locale.resource['txt.download'],
            dataIndex: 'downloadPath',
            render: (path) => (
              <Button target="_blank" href={path}>
                {locale.resource['txt.download']}
              </Button>
            ),
          },
        ]}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...res}
      />
    </>
  );
}

export default Download;
